/**
 * <ta-add-pitcher> — prominent "+ Add Pitcher" button + modal form.
 *
 * Opens a native <dialog> with: First, Last, Athlete age, Playing age,
 * Throws, Nickname. On submit, creates the athlete and navigates to
 * /athletes/:id (lands on the Dashboards tab; if no data yet, dashboards
 * shows the "no data uploaded yet" empty state — but the Profile tab
 * already shows the athlete's basic info immediately).
 *
 * No properties to set — drop the element wherever a "+ Add Pitcher"
 * button should appear.
 *
 * Emits 'athletes-imported' (bubbling) on success — main.js listens
 * and refreshes the home view's athletes list before navigation kicks in.
 */

import { createAthlete } from '../../db/queries.js';

class TaAddPitcher extends HTMLElement {
  constructor() {
    super();
    this._open = false;
    this._submitting = false;
    this._error = null;
  }

  connectedCallback() {
    this.render();
    this._onClick = (e) => this.handleClick(e);
    this._onSubmit = (e) => this.handleSubmit(e);
    this.addEventListener('click', this._onClick);
    this.addEventListener('submit', this._onSubmit);
  }

  handleClick(e) {
    if (e.target.closest('.ta-add-pitcher__open')) {
      e.preventDefault();
      this._open = true;
      this._error = null;
      this.render();
      this.querySelector('dialog')?.showModal();
      // focus first input for fast typing
      this.querySelector('input[name="first_name"]')?.focus();
    } else if (e.target.closest('.ta-add-pitcher__cancel')) {
      e.preventDefault();
      this.close();
    }
  }

  close() {
    this._open = false;
    this.querySelector('dialog')?.close();
    this.render();
  }

  async handleSubmit(e) {
    if (!e.target.matches('.ta-add-pitcher__form')) return;
    e.preventDefault();
    if (this._submitting) return;

    const fd = new FormData(e.target);
    const payload = {
      first_name: fd.get('first_name'),
      last_name: fd.get('last_name'),
      athlete_age: fd.get('athlete_age'),
      playing_age: fd.get('playing_age'),
      throws_hand: fd.get('throws_hand') || 'R',
      nickname: fd.get('nickname'),
    };

    this._submitting = true;
    this._error = null;
    this.render();
    this.querySelector('dialog')?.showModal();   // re-show after re-render

    try {
      const athlete = await createAthlete(payload);
      this._submitting = false;
      this.close();
      // Tell the host to refresh the athletes list, then navigate to the
      // new pitcher's profile.
      this.dispatchEvent(new CustomEvent('athletes-imported', { bubbles: true }));
      // Default landing tab is dashboards; the empty state guides the
      // coach to the upload zone if no data yet.
      location.hash = `#/athletes/${athlete.id}`;
    } catch (err) {
      this._submitting = false;
      // UNIQUE collision (Postgres 23505) is the most useful specific case
      const msg = /duplicate key|23505/i.test(err.message ?? '')
        ? `An athlete named "${payload.first_name} ${payload.last_name}" already exists.`
        : (err.message ?? String(err));
      this._error = msg;
      this.render();
      this.querySelector('dialog')?.showModal();
    }
  }

  render() {
    this.innerHTML = `
      <button type="button" class="ta-add-pitcher__open">+ Add Pitcher</button>
      <dialog class="ta-add-pitcher__modal">
        ${this._open ? this.renderForm() : ''}
      </dialog>
    `;
  }

  renderForm() {
    return `
      <form method="dialog" class="ta-add-pitcher__form">
        <h3 class="ta-add-pitcher__title">Add pitcher</h3>
        <p class="ta-add-pitcher__sub">
          Just first + last is enough. Add ages now or later — nothing requires DOB.
        </p>

        ${this._error ? `<div class="ta-add-pitcher__error">${escapeHtml(this._error)}</div>` : ''}

        <div class="form-row">
          <label class="form-field">
            <span class="form-field__label">First name</span>
            <input type="text" name="first_name" required autocomplete="off">
          </label>
          <label class="form-field">
            <span class="form-field__label">Last name</span>
            <input type="text" name="last_name" required autocomplete="off">
          </label>
        </div>

        <div class="form-row">
          <label class="form-field">
            <span class="form-field__label">Athlete age <span class="form-field__optional">(years, optional)</span></span>
            <input type="number" name="athlete_age" min="5" max="60" placeholder="e.g. 13">
          </label>
          <label class="form-field">
            <span class="form-field__label">Playing age <span class="form-field__optional">(competitive class, optional)</span></span>
            <input type="text" name="playing_age" placeholder="e.g. 13U, 14U, HS">
          </label>
        </div>

        <div class="form-row">
          <fieldset class="form-field throws-toggle">
            <legend class="form-field__label">Throws</legend>
            <label><input type="radio" name="throws_hand" value="R" checked> R</label>
            <label><input type="radio" name="throws_hand" value="L"> L</label>
            <label><input type="radio" name="throws_hand" value="S"> S</label>
          </fieldset>
          <label class="form-field">
            <span class="form-field__label">Nickname <span class="form-field__optional">(optional)</span></span>
            <input type="text" name="nickname" placeholder="e.g. TT" autocomplete="off">
          </label>
        </div>

        <div class="form-actions">
          <button type="button" class="ta-add-pitcher__cancel" ${this._submitting ? 'disabled' : ''}>Cancel</button>
          <button type="submit" class="ta-add-pitcher__submit" ${this._submitting ? 'disabled' : ''}>
            ${this._submitting ? 'Creating…' : 'Create pitcher'}
          </button>
        </div>
      </form>
    `;
  }
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

customElements.define('ta-add-pitcher', TaAddPitcher);
